import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ModuleNavigationContext } from './ModuleNavigation.jsx';
import SidebarLink from './SidebarLink.jsx';
import ProfileMenu from './ProfileMenu.jsx';
import { activateDialog } from '../../lib/dialogFocus.js';
import { enableSheetDrag } from '../../lib/sheet.js';
import { useNavigationIndicators } from './useNavigationIndicators.js';
import { useMediaQuery as useMedia } from '../../hooks/useMediaQuery.js';
import '../../css/navigation-motion.css';
import '../../css/lm-brand.css';
import '../../css/navigation-shell.css';

const HOME = { id: 'inicio', path: '/', title: 'Inicio', icon: 'home' };

/** Shared presentation only: each portal supplies its own authorized modules and session. */
export default function AppLayout({ session, modules, onLogout, external = false, children }) {
  const { pathname } = useLocation();
  const mobile = useMedia('(max-width: 760px)');
  const compactTabs = useMedia('(max-width: 1150px)');
  const sidebarStorageKey = external ? 'lastmile_proveedor_sidebar_collapsed' : 'lastmile_sidebar_collapsed';
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(sidebarStorageKey) !== 'false';
    } catch (error) {
      console.warn('No se pudo leer la preferencia de la barra lateral.', error);
      return true;
    }
  });
  const [moreOpen, setMoreOpen] = useState(false);
  const [headerTarget, setHeaderTarget] = useState(null);
  const [contentTarget, setContentTarget] = useState(null);
  const [hasNavigation, setHasNavigation] = useState(false);
  const sidebar = useRef(null);
  const moreOverlay = useRef(null);
  const content = useRef(null);
  const header = useRef(null);
  const activeModule = modules.find(module => pathname === module.path || pathname.startsWith(`${module.path}/`));
  const operation = modules.filter(module => ['bigitem', 'reportedrivers'].includes(module.id));
  const management = modules.filter(module => !['bigitem', 'reportedrivers'].includes(module.id));
  const ordered = [HOME, ...operation, ...management];
  const direct = ordered.length > 5 ? ordered.slice(0, 4) : ordered;
  const extra = ordered.slice(direct.length);
  const shell = useRef(null);
  const [viewIcon, setViewIcon] = useState(null);
  useNavigationIndicators(shell, pathname, setViewIcon);
  const navigationContext = useMemo(() => ({
    target: compactTabs ? contentTarget : headerTarget, register: setHasNavigation, icon: viewIcon || activeModule?.icon,
  }), [compactTabs, contentTarget, headerTarget, viewIcon, activeModule?.icon]);

  useEffect(() => { setMoreOpen(false); }, [pathname, mobile]);
  useEffect(() => {
    if (!mobile || !moreOpen || !extra.length) return undefined;
    const release = activateDialog(sidebar.current, { onClose: () => setMoreOpen(false) });
    // Mismo asa y arrastre que los modales (lib/sheet.js); "Mas" cierra al instante, sin animacion de salida.
    const stopDrag = enableSheetDrag(moreOverlay.current, () => () => setMoreOpen(false));
    const background = [header.current, content.current, shell.current.querySelector('.navigation-dock')];
    background.forEach(node => { node.inert = true; });
    return () => { background.forEach(node => { node.inert = false; }); stopDrag(); release(); };
  }, [mobile, moreOpen, extra.length]);

  function toggleCollapsed() {
    const nextCollapsed = !collapsed;
    setCollapsed(nextCollapsed);
    try {
      localStorage.setItem(sidebarStorageKey, String(nextCollapsed));
    } catch (error) {
      console.warn('No se pudo guardar la preferencia de la barra lateral.', error);
    }
  }

  function links(items) {
    return items.map(module => <SidebarLink key={module.id} module={module} collapsed={collapsed && !mobile}
      onNavigate={() => setMoreOpen(false)} />);
  }

  return <ModuleNavigationContext.Provider value={navigationContext}>
    <div ref={shell} className={`navigation-shell${collapsed ? ' is-collapsed' : ''}`}>
      {!mobile && <aside id="module-sidebar" className="navigation-sidebar" aria-label="Navegación principal">
        <nav className="navigation-modules" aria-label="Módulos">
          {links([HOME])}
          {operation.length > 0 && <><p className="navigation-group">Operación</p>{links(operation)}</>}
          {management.length > 0 && <><hr className="navigation-separator" /><p className="navigation-group">Gestión</p>{links(management)}</>}
        </nav>
        <button type="button" className="navigation-collapse" aria-controls="module-sidebar" aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expandir barra lateral' : 'Contraer barra lateral'} onClick={toggleCollapsed}>
          <span className="navigation-icon material-symbols-rounded" aria-hidden="true">{collapsed ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left'}</span>
          <span className="navigation-label">Contraer menú</span>
        </button>
      </aside>}
      <div className="navigation-workspace">
        <header ref={header} className={`navigation-topbar${hasNavigation ? ' has-module-navigation' : ''}`}>
          <div className="navigation-current">
            <Link to="/" className="navigation-app-brand" aria-label="Last Mile 360 · Inicio">
              <span className="lm-mark" aria-hidden="true"><span className="lm-monogram"><span className="lm-letter-l" /><span className="lm-letter-m" /><span className="lm-arrow" /></span></span>
              <strong>Last Mile <span>360</span></strong>
            </Link>
          </div>
          <div ref={setHeaderTarget} className="navigation-header-tabs" hidden={compactTabs || !hasNavigation} />
          <ProfileMenu session={session} onLogout={onLogout} external={external} />
        </header>
        <main ref={content} id="main-content" tabIndex={-1} className="navigation-main">
          <div ref={setContentTarget} className="navigation-content-tabs" hidden={!compactTabs || !hasNavigation} />
          {children}
        </main>
      </div>
      {mobile && <nav className="navigation-dock" aria-label="Navegación principal móvil">
        {direct.map(module => <NavLink key={module.id} to={module.path} end={module.path === '/'} aria-label={module.title}>
          <span className="material-symbols-rounded" aria-hidden="true">{module.icon}</span>
          <span>{{ inicio: 'Inicio', bigitem: 'Big Item', reportedrivers: 'Drivers', catalogos: 'Catálogos' }[module.id] || module.title}</span>
        </NavLink>)}
        {extra.length > 0 && <button type="button" aria-label="Más módulos" aria-haspopup="dialog"
          aria-expanded={moreOpen} aria-current={extra.includes(activeModule) ? 'page' : undefined} onClick={() => setMoreOpen(true)}>
          <span className="material-symbols-rounded" aria-hidden="true">more_horiz</span><span>Más</span>
        </button>}
      </nav>}
      {mobile && moreOpen && extra.length > 0 && <div ref={moreOverlay} className="navigation-more-overlay" onClick={event => {
        if (event.target === event.currentTarget) setMoreOpen(false);
      }}><section ref={sidebar} className="navigation-more" role="dialog" aria-modal="true" aria-labelledby="more-modules-title">
        <div className="sheet-handle" aria-hidden="true" />
        <div className="modal-header"><h3 id="more-modules-title" className="modal-title">Más módulos</h3>
          <button type="button" className="btn btn-danger icon-btn" aria-label="Cerrar Más" onClick={() => setMoreOpen(false)}><span className="material-symbols-rounded" aria-hidden="true">close</span></button>
        </div><nav className="navigation-more-links" aria-label="Otros módulos">{links(extra)}</nav>
      </section></div>}
    </div>
  </ModuleNavigationContext.Provider>;
}
